import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Badge,
  List,
  ListItem,
  ListIcon,
  Button,
  VStack,
  HStack,
  Image,
  useDisclosure,
  Collapse,
  Grid,
  GridItem,
  chakra,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaCheck,
  FaPlus,
  FaMinus,
  FaCog,
  FaRuler,
  FaMobile,
  FaLeaf,
  FaChartLine,
  FaExpand,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { COLORS } from "../../globalvariables";
import { useLanguage } from "../../context/LanguageContext";
import { texts } from "../../lang";

const MotionBox = chakra(motion.div);
const MotionImage = chakra(motion.img);

const MachineModelCard = ({
  title,
  description,
  image,
  specs,
  isPremium,
  animationDelay = 0,
  // New gallery props
  hasGallery = false,
  galleryImages = [],
  currentIndex = 0,
  setCurrentIndex = () => {},
  onOpenGallery = () => {},
  // New prop for scroll functionality
  modelId = null,
}) => {
  const { language } = useLanguage();
  const machineImages = texts[language].images.machines;
  const { isOpen, onToggle } = useDisclosure();
  const [isHovered, setIsHovered] = useState(false);
  const [showGalleryControls, setShowGalleryControls] = useState(false);
  const boxRef = useRef();

  // Ne pas verrouiller le scroll global pour l'ouverture des spécifications
  // (le verrouillage doit rester géré uniquement par les modales plein écran)

  // Function to scroll to the corresponding model section
  const scrollToModelSection = () => {
    if (modelId) {
      // Create the ID for the element we want to scroll to
      const targetId = `model-${modelId}`;
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        // Scroll to the element with smooth behavior
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: animationDelay,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hover: {
      scale: 1.05,
      filter: "brightness(1.1)",
      transition: { duration: 0.3 },
    },
    tap: {
      scale: 1.03,
      transition: { duration: 0.1 },
    },
  };

  // Map category to appropriate icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case "Caractéristiques Techniques":
      case "Technical Specs":
        return FaCog;
      case "Dimensions & Poids":
      case "Dimensions & Weight":
        return FaRuler;
      case "Système Intelligent":
      case "Smart System":
        return FaMobile;
      case "Performance":
        return FaChartLine;
      case "Services":
      case "Service":
        return FaLeaf;
      default:
        return FaCheck;
    }
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (galleryImages && galleryImages.length > 0) {
      setCurrentIndex((prev) =>
        prev === 0 ? galleryImages.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (galleryImages && galleryImages.length > 0) {
      setCurrentIndex((prev) =>
        prev === galleryImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  return (
    <MotionBox
      id={modelId ? `model-${modelId}` : undefined}
      ref={boxRef}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      w="100%"
      maxW="1200px"
      bg="rgba(26, 26, 26, 0.7)"
      borderRadius="xl"
      overflow="hidden"
      boxShadow={isHovered ? COLORS.cardGlow : "none"}
      transition="box-shadow 0.3s ease"
      position="relative"
      mb={{ base: 6, md: 10 }}
      borderWidth="1px"
      borderColor={isHovered ? COLORS.primary : "transparent"}
    >
      {/* Premium badge */}
      {isPremium && (
        <Badge
          position="absolute"
          top={{ base: "15px", md: "20px" }}
          right={{ base: "-25px", md: "-30px" }}
          transform="rotate(45deg)"
          px={{ base: 4, md: 6 }}
          py={1}
          fontSize={{ base: "xs", md: "sm" }}
          bg={COLORS.primary}
          color="black"
          fontWeight="bold"
          zIndex={1}
        >
          PREMIUM
        </Badge>
      )}

      {/* Main content */}
      <Flex direction={{ base: "column", lg: "row" }} w="100%">
        {/* Machine image with gallery functionality */}
        <MotionBox
          w={{ base: "100%", lg: "40%" }}
          h={{ base: "250px", md: "400px", lg: "450px" }}
          position="relative"
          overflow="hidden"
          variants={imageVariants}
          whileHover="hover"
          whileTap="tap"
          onMouseEnter={() => setShowGalleryControls(true)}
          onMouseLeave={() => setShowGalleryControls(false)}
        >
          <MotionImage
            src={image || machineImages.placeholder}
            alt={title}
            objectFit="cover"
            objectPosition="center"
            w="100%"
            h="100%"
            animate={{
              scale: showGalleryControls ? 1.02 : 1,
              transition: { duration: 0.3 },
            }}
          />

          {/* Gradient overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)"
            pointerEvents="none"
          />

          {/* Gallery controls - only show when hasGallery is true */}
          {hasGallery && (
            <>
              {/* Fullscreen button */}
              <IconButton
                icon={<FaExpand />}
                aria-label="View Fullscreen"
                position="absolute"
                top={{ base: "8px", md: "10px" }}
                right={{ base: "8px", md: "10px" }}
                size={{ base: "sm", md: "md" }}
                colorScheme="blackAlpha"
                opacity={showGalleryControls ? 0.9 : 0}
                transition="opacity 0.3s ease"
                onClick={onOpenGallery}
                zIndex={3}
                _hover={{ opacity: 1, bg: COLORS.primary, color: "black" }}
              />

              {/* Navigation arrows for gallery */}
              <Flex
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                justify="space-between"
                align="center"
                px={{ base: 2, md: 3 }}
                opacity={showGalleryControls ? 1 : 0}
                transition="opacity 0.3s ease"
                zIndex={2}
              >
                <IconButton
                  icon={<FaChevronLeft />}
                  aria-label="Previous Image"
                  onClick={handlePrevImage}
                  variant="solid"
                  colorScheme="blackAlpha"
                  size={{ base: "sm", md: "md" }}
                  opacity={0.7}
                  _hover={{ opacity: 1, bg: COLORS.primary, color: "black" }}
                />
                <IconButton
                  icon={<FaChevronRight />}
                  aria-label="Next Image"
                  onClick={handleNextImage}
                  variant="solid"
                  colorScheme="blackAlpha"
                  size={{ base: "sm", md: "md" }}
                  opacity={0.7}
                  _hover={{ opacity: 1, bg: COLORS.primary, color: "black" }}
                />
              </Flex>

              {/* Thumbnails at the bottom */}

              <Flex
                position="absolute"
                bottom={{ base: "8px", md: "10px" }}
                left="0"
                right="0"
                px={{ base: 3, md: 4 }}
                py={2}
                bg="rgba(0,0,0,0.6)" // ✅ background noir maintenu
                opacity={showGalleryControls ? 1 : 0}
                transition="opacity 0.3s ease"
                zIndex={3}
                overflowX="auto"
                gap={2}
                justify="flex-start"
                scrollSnapType="x mandatory"
              >
                {galleryImages.map((img, idx) => (
                  <Box
                    key={idx}
                    cursor="pointer"
                    borderWidth="2px"
                    borderColor={
                      idx === currentIndex ? COLORS.primary : "transparent"
                    }
                    borderRadius="md"
                    overflow="hidden"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(idx);
                    }}
                    transition="all 0.2s"
                    opacity={idx === currentIndex ? 1 : 0.6}
                    _hover={{ opacity: 0.9 }}
                    flexShrink={0}
                    scrollSnapAlign="start"
                  >
                    <Image
                      src={img}
                      alt={`${title} ${idx + 1}`}
                      h={{ base: "50px", md: "60px" }} // ↗️ plus haut
                      w={{ base: "80px", md: "100px" }}
                      objectFit="cover"
                    />
                  </Box>
                ))}
              </Flex>

              {/* Image counter */}
              <Box
                position="absolute"
                top={{ base: "8px", md: "10px" }}
                left={{ base: "8px", md: "10px" }}
                bg="rgba(0,0,0,0.6)"
                color="white"
                px={2}
                py={1}
                borderRadius="md"
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="medium"
                opacity={showGalleryControls ? 0.9 : 0}
                transition="opacity 0.3s ease"
                zIndex={3}
              >
                {currentIndex + 1} / {galleryImages.length}
              </Box>
            </>
          )}
        </MotionBox>

        {/* Machine info */}
        <VStack
          w={{ base: "100%", lg: "60%" }}
          align="flex-start"
          p={{ base: 4, md: 8 }}
          spacing={{ base: 4, md: 6 }}
        >
          <Heading
            size={{ base: "lg", md: "xl" }}
            color="white"
            position="relative"
            bgGradient={`linear(to-r, white, ${COLORS.primary} 50%, white)`}
            bgClip="text"
            fontSize={{ base: "2xl", md: "4xl" }}
            _after={{
              content: '""',
              position: "absolute",
              bottom: "-10px",
              left: "0",
              width: { base: "80px", md: "100px" },
              height: "3px",
              bg: COLORS.primary,
            }}
          >
            {title}
          </Heading>

          <Text color="gray.300" fontSize={{ base: "md", md: "lg" }}>
            {description}
          </Text>

          {/* Preview specs */}
          <Grid
            templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
            gap={{ base: 3, md: 4 }}
            w="100%"
            mb={{ base: 3, md: 4 }}
          >
            {specs.highlights &&
              specs.highlights.map((highlight, idx) => (
                <HStack key={idx} spacing={3}>
                  <Icon as={FaCheck} color={COLORS.primary} boxSize={{ base: 3, md: 4 }} />
                  <Text color="white" fontSize={{ base: "sm", md: "md" }}>{highlight}</Text>
                </HStack>
              ))}
          </Grid>

          {/* Move the button down and center it */}
          <Flex w="100%" justify="center" mt={{ base: 6, md: 10 }}>
            <Button
              onClick={onToggle}
              leftIcon={isOpen ? <FaMinus /> : <FaPlus />}
              color={isOpen ? COLORS.creamText : COLORS.creamText}
              borderColor={isOpen ? COLORS.primary : COLORS.creamText}
              bg={COLORS.primary}
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "sm", md: "md" }}
              _hover={{
                color: COLORS.creamText,
                background: COLORS.primaryHover,
              }}
            >
              {isOpen
                ? language === "fr"
                  ? "Masquer les détails"
                  : "Hide Details"
                : language === "fr"
                ? "Voir toutes les spécifications"
                : "Show Full Specifications"}
            </Button>
          </Flex>
        </VStack>
      </Flex>

      {/* Expandable specs section */}
      <Collapse in={isOpen} animateOpacity>
        <Box p={{ base: 4, md: 8 }} bg="rgba(0,0,0,0.3)">
          <Grid
            templateColumns={{
              base: "repeat(1, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={{ base: 6, md: 8 }}
          >
            {specs.categories &&
              specs.categories.map((category, idx) => (
                <GridItem key={idx}>
                  <VStack align="flex-start" spacing={{ base: 3, md: 4 }}>
                    <HStack>
                      <Icon
                        as={getCategoryIcon(category.name)}
                        color={COLORS.primary}
                        boxSize={{ base: 4, md: 5 }}
                      />
                      <Heading size={{ base: "sm", md: "md" }} color="white">
                        {category.name}
                      </Heading>
                    </HStack>

                    <List spacing={{ base: 2, md: 3 }} w="100%">
                      {category.items.map((item, itemIdx) => (
                        <ListItem key={itemIdx} display="flex">
                          <ListIcon
                            as={FaCheck}
                            color={COLORS.primary}
                            mt={1}
                          />
                          <Text color="white" fontSize={{ base: "sm", md: "md" }}>
                            <strong>{item.name}:</strong> {item.value}
                          </Text>
                        </ListItem>
                      ))}
                    </List>
                  </VStack>
                </GridItem>
              ))}
          </Grid>
        </Box>
      </Collapse>
    </MotionBox>
  );
};

export default MachineModelCard;
